import { useState, useEffect, useRef } from 'react'
import { slackApi, productManagersApi } from '../../../api/client'

interface SlackNotification {
  id: number
  notification_key: string
  name: string
  description: string
  enabled: boolean
  schedule_day: number
  schedule_hour: number
  schedule_minute: number
  timezone: string
  channels_json: string
  mention_pms: boolean
  notification_type: 'slack' | 'email'
  email_recipients_json: string
  email_cc_json: string
  external_schedule: string | null
  last_run_at: string | null
}

interface ProductManager {
  id: number
  name: string
  email: string
  slack_user_id: string | null
  slack_username: string | null
  is_active: boolean
}

interface SlackChannel {
  id: string
  name: string
  is_archived: boolean
  num_members: number
}

const DAY_LABELS = ['', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday']

const NOTIFICATION_DESCRIPTIONS: Record<string, string> = {
  presales_calendar: 'Lists all companies with production key handovers, go-live dates, or custom feature deadlines in the next two months.',
  daon_weekly_update: 'Summarises upcoming clients using Daon IDV — their key dates, IDV configuration, and any Daon-specific notes.',
}

/** Convert UTC hour+minute + day to Melbourne local time display string */
function formatScheduleLocal(day: number, hour: number, minute: number, tz: string): string {
  const now = new Date()
  const utcDate = new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate(), hour, minute))
  try {
    const localStr = utcDate.toLocaleString('en-AU', { timeZone: tz, hour: 'numeric', minute: '2-digit', hour12: true })
    const dayLabel = day === 0 ? 'Every day' : `${DAY_LABELS[day]}s`
    return `${dayLabel} at ${localStr} (${tz.split('/')[1]})`
  } catch {
    const dayLabel = day === 0 ? 'Every day' : `${DAY_LABELS[day]}s`
    return `${dayLabel} at ${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')} UTC`
  }
}

export function SchedulerTool() {
  const [notifications, setNotifications] = useState<SlackNotification[]>([])
  const [editingId, setEditingId] = useState<number | null>(null)
  const [editDraft, setEditDraft] = useState<Partial<SlackNotification>>({})
  const [editChannels, setEditChannels] = useState<string[]>([])
  const [editEmailRecipients, setEditEmailRecipients] = useState<string[]>([])
  const [newEmailInput, setNewEmailInput] = useState('')
  const [editEmailCc, setEditEmailCc] = useState<string[]>([])
  const [newCcInput, setNewCcInput] = useState('')
  const [productManagers, setProductManagers] = useState<ProductManager[]>([])
  const [availableChannels, setAvailableChannels] = useState<SlackChannel[]>([])
  const [channelSearch, setChannelSearch] = useState('')
  const [channelIdInput, setChannelIdInput] = useState('')
  const [activeTab, setActiveTab] = useState<'notifications' | 'pms'>('notifications')
  const [_loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [actionLoading, setActionLoading] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)

  // Preview modal state
  const [previewHtml, setPreviewHtml] = useState<string | null>(null)
  const [previewLoading, setPreviewLoading] = useState(false)
  const previewIframeRef = useRef<HTMLIFrameElement>(null)

  useEffect(() => {
    loadNotifications()
    loadProductManagers()
  }, [])

  // Auto-dismiss success messages
  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(null), 4000)
      return () => clearTimeout(timer)
    }
  }, [success])

  const loadNotifications = async () => {
    try {
      const response = await slackApi.getNotifications()
      if (response.success && response.data) {
        const apiData = response.data as any
        const items = apiData.data || apiData
        setNotifications(Array.isArray(items) ? items : [])
      }
    } catch (err) {
      console.error('Failed to load notifications:', err)
    }
  }

  const loadProductManagers = async () => {
    try {
      const response = await productManagersApi.list()
      if (response.success && response.data) {
        setProductManagers(response.data.items)
      }
    } catch (err) {
      console.error('Failed to load product managers:', err)
    }
  }

  const loadSlackChannels = async () => {
    if (availableChannels.length > 0) return
    setLoading(true)
    try {
      const response = await slackApi.listChannels()
      if (response.success && response.data) {
        const apiResponse = response.data as any
        if (apiResponse.success && Array.isArray(apiResponse.data)) {
          setAvailableChannels(apiResponse.data)
        }
      }
    } catch (err) {
      console.error('Failed to load channels:', err)
    } finally {
      setLoading(false)
    }
  }

  const startEditing = (notif: SlackNotification) => {
    setEditingId(notif.id)
    setEditDraft({
      enabled: notif.enabled,
      schedule_day: notif.schedule_day,
      schedule_hour: notif.schedule_hour,
      schedule_minute: notif.schedule_minute,
      mention_pms: notif.mention_pms,
    })
    setEditChannels(JSON.parse(notif.channels_json || '[]'))
    setEditEmailRecipients(JSON.parse(notif.email_recipients_json || '[]'))
    setEditEmailCc(JSON.parse(notif.email_cc_json || '[]'))
    setNewEmailInput('')
    setNewCcInput('')
    setChannelSearch('')
    if (notif.notification_type !== 'email') {
      loadSlackChannels()
    }
  }

  const cancelEditing = () => {
    setEditingId(null)
    setEditDraft({})
    setEditChannels([])
    setEditEmailRecipients([])
    setEditEmailCc([])
    setNewEmailInput('')
    setNewCcInput('')
    setChannelSearch('')
    setChannelIdInput('')
  }

  const saveNotification = async (id: number) => {
    const notif = notifications.find((n) => n.id === id)
    setSaving(true)
    setError(null)
    try {
      const payload: any = {
        ...editDraft,
      }
      if (notif?.notification_type === 'email') {
        payload.email_recipients_json = JSON.stringify(editEmailRecipients)
        payload.email_cc_json = JSON.stringify(editEmailCc)
      } else {
        payload.channels_json = JSON.stringify(editChannels)
      }

      const response = await slackApi.updateNotification(id, payload)
      if (response.success) {
        setSuccess('Settings saved')
        setEditingId(null)
        loadNotifications()
      } else {
        setError(response.error || 'Failed to save')
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to save')
    } finally {
      setSaving(false)
    }
  }

  const TEST_CHANNEL = 'C0AJURPA4SG'

  const handleTest = async (id: number, notifType?: string) => {
    setActionLoading(`test-${id}`)
    setError(null)
    try {
      // For Slack notifications, send the real report to the test channel
      const isSlack = notifType === 'slack' || !notifType
      const response = isSlack
        ? await slackApi.triggerNotification(id, TEST_CHANNEL)
        : await slackApi.testNotification(id)
      if (response.success) {
        const data = response.data as any
        setSuccess(data?.data?.message || data?.message || 'Test sent')
      } else {
        setError(response.error || 'Test failed')
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Test failed')
    } finally {
      setActionLoading(null)
    }
  }

  const handleTrigger = async (id: number) => {
    setActionLoading(`trigger-${id}`)
    setError(null)
    try {
      // Pressing "Send now" is deliberate, so it runs even when the
      // notification is switched off for the schedule.
      const response = await slackApi.triggerNotification(id, undefined, true)
      if (response.success) {
        const data = response.data as any
        setSuccess(data?.data?.message || data?.message || 'Notification sent')
        loadNotifications()
      } else {
        setError(response.error || 'Trigger failed')
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Trigger failed')
    } finally {
      setActionLoading(null)
    }
  }

  const handlePreview = async (id: number) => {
    setPreviewLoading(true)
    setError(null)
    try {
      const response = await slackApi.previewNotification(id)
      if (response.success && response.data) {
        const data = response.data as any
        const html = data?.data?.html || data?.html
        if (html) {
          setPreviewHtml(html)
        } else {
          setError('No preview content returned')
        }
      } else {
        setError(response.error || 'Failed to load preview')
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load preview')
    } finally {
      setPreviewLoading(false)
    }
  }

  const addEmailRecipient = () => {
    const email = newEmailInput.trim().toLowerCase()
    if (!email) return
    // Basic email validation
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError('Invalid email address')
      return
    }
    if (editEmailRecipients.includes(email)) {
      setError('Email already added')
      return
    }
    setEditEmailRecipients([...editEmailRecipients, email])
    setNewEmailInput('')
  }

  const addCcRecipient = () => {
    const email = newCcInput.trim().toLowerCase()
    if (!email) return
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError('Invalid email address')
      return
    }
    if (editEmailCc.includes(email)) {
      setError('Email already added to CC')
      return
    }
    setEditEmailCc([...editEmailCc, email])
    setNewCcInput('')
  }

  const getChannelName = (channelId: string): string => {
    const ch = availableChannels.find((c) => c.id === channelId)
    return ch ? `#${ch.name}` : channelId
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-lg font-semibold text-[var(--text-primary)]">Scheduler</h2>
        <p className="text-sm text-[var(--text-muted)] mt-1">
          Manage automated Slack and email notifications — calendar summaries, weekly updates, and scheduled reports.
        </p>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-[var(--border-color)]">
        {[
          { id: 'notifications' as const, label: 'Notifications' },
          { id: 'pms' as const, label: 'Key People' },
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === tab.id
                ? 'border-primary text-primary'
                : 'border-transparent text-[var(--text-muted)] hover:text-[var(--text-primary)]'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Error/Success Messages */}
      {error && (
        <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm">{error}</div>
      )}
      {success && (
        <div className="p-3 bg-green-500/10 border border-green-500/30 rounded-lg text-green-400 text-sm">{success}</div>
      )}

      {/* Notifications Tab */}
      {activeTab === 'notifications' && (
        <div className="space-y-4">
          {notifications.length === 0 && (
            <p className="text-sm text-[var(--text-muted)]">No notifications configured. Run the database migration first.</p>
          )}

          {notifications.map((notif) => {
            const isEditing = editingId === notif.id
            const isEmail = notif.notification_type === 'email'
            const channels: string[] = JSON.parse(notif.channels_json || '[]')
            const emailRecipients: string[] = JSON.parse(notif.email_recipients_json || '[]')
            const emailCc: string[] = JSON.parse(notif.email_cc_json || '[]')

            return (
              <div key={notif.id} className="border border-[var(--border-color)] rounded-lg overflow-hidden">
                {/* Card Header */}
                <div className="p-4 bg-[var(--bg-secondary)]">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${(notif.enabled || notif.external_schedule) ? 'bg-green-500' : 'bg-gray-500'}`} />
                        <h3 className="text-sm font-semibold text-[var(--text-primary)] truncate">{notif.name}</h3>
                        {isEmail && (
                          <span className="px-1.5 py-0.5 text-[10px] font-medium bg-blue-500/20 text-blue-400 rounded uppercase tracking-wide">
                            Email
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-[var(--text-muted)] ml-4.5 mb-2">
                        {NOTIFICATION_DESCRIPTIONS[notif.notification_key] || notif.description}
                      </p>
                      <p className="text-xs text-[var(--text-muted)] ml-4.5">
                        {notif.external_schedule || formatScheduleLocal(notif.schedule_day, notif.schedule_hour, notif.schedule_minute, notif.timezone)}
                        {isEmail
                          ? emailRecipients.length > 0 && ` → ${emailRecipients.length} recipient${emailRecipients.length > 1 ? 's' : ''}${emailCc.length > 0 ? ` + ${emailCc.length} CC` : ''}`
                          : channels.length > 0 && ` → ${channels.length} channel${channels.length > 1 ? 's' : ''}`
                        }
                      </p>
                      {notif.last_run_at && (
                        <p className="text-xs text-[var(--text-muted)] ml-4.5 mt-0.5">
                          Last sent: {new Date(notif.last_run_at + 'Z').toLocaleString()}
                        </p>
                      )}
                    </div>
                    <div className="flex gap-2 flex-shrink-0">
                      {!isEditing && (
                        <>
                          <button
                            onClick={() => startEditing(notif)}
                            className="px-3 py-1.5 text-xs font-medium bg-[var(--bg-tertiary)] text-[var(--text-primary)] rounded-md hover:bg-[var(--border-color)] transition-colors"
                          >
                            Edit
                          </button>
                          {isEmail ? (
                            <>
                              <button
                                onClick={() => handlePreview(notif.id)}
                                disabled={previewLoading}
                                title="Preview the email content that would be sent"
                                className="px-3 py-1.5 text-xs font-medium bg-purple-600/20 text-purple-400 rounded-md hover:bg-purple-600/30 transition-colors disabled:opacity-50"
                              >
                                {previewLoading ? '...' : 'Preview'}
                              </button>
                              <button
                                onClick={() => handleTrigger(notif.id)}
                                disabled={actionLoading === `trigger-${notif.id}`}
                                title="Send the email to the configured recipients now"
                                className="px-3 py-1.5 text-xs font-medium bg-primary/20 text-primary rounded-md hover:bg-primary/30 transition-colors disabled:opacity-50"
                              >
                                {actionLoading === `trigger-${notif.id}` ? '...' : 'Send Now'}
                              </button>
                            </>
                          ) : (
                            <>
                              <button
                                onClick={() => handlePreview(notif.id)}
                                disabled={previewLoading}
                                title="Preview the message content that would be posted"
                                className="px-3 py-1.5 text-xs font-medium bg-purple-600/20 text-purple-400 rounded-md hover:bg-purple-600/30 transition-colors disabled:opacity-50"
                              >
                                {previewLoading ? '...' : 'Preview'}
                              </button>
                              <button
                                onClick={() => handleTest(notif.id, notif.notification_type)}
                                disabled={actionLoading === `test-${notif.id}`}
                                title="Sends the full report to #alitia-pub-test for review"
                                className="px-3 py-1.5 text-xs font-medium bg-blue-600/20 text-blue-400 rounded-md hover:bg-blue-600/30 transition-colors disabled:opacity-50"
                              >
                                {actionLoading === `test-${notif.id}` ? '...' : 'Test'}
                              </button>
                              <button
                                onClick={() => handleTrigger(notif.id)}
                                disabled={actionLoading === `trigger-${notif.id}`}
                                title={JSON.parse(notif.channels_json || '[]').includes('C04PFKAM8JW') ? 'Sends the full report to #team-implementations-presales' : 'Posts the full notification to the configured Slack channels'}
                                className="px-3 py-1.5 text-xs font-medium bg-primary/20 text-primary rounded-md hover:bg-primary/30 transition-colors disabled:opacity-50"
                              >
                                {actionLoading === `trigger-${notif.id}` ? '...' : 'Send Now'}
                              </button>
                            </>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                </div>

                {/* Editing Panel */}
                {isEditing && (
                  <div className="p-4 space-y-4 border-t border-[var(--border-color)]">
                    {/* Enabled toggle — hidden for externally scheduled */}
                    {!notif.external_schedule && (
                      <label className="flex items-center gap-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={editDraft.enabled ?? false}
                          onChange={(e) => setEditDraft({ ...editDraft, enabled: e.target.checked })}
                          className="w-4 h-4 rounded border-gray-300"
                        />
                        <span className="text-sm text-[var(--text-primary)]">Enabled</span>
                      </label>
                    )}

                    {/* Schedule */}
                    {notif.external_schedule ? (
                      <div>
                        <label className="block text-xs font-medium text-[var(--text-muted)] mb-1.5 uppercase tracking-wide">Schedule</label>
                        <p className="text-sm text-[var(--text-primary)]">{notif.external_schedule}</p>
                      </div>
                    ) : (
                    <div>
                      <label className="block text-xs font-medium text-[var(--text-muted)] mb-1.5 uppercase tracking-wide">Schedule</label>
                      <div className="flex gap-2">
                        <select
                          value={editDraft.schedule_day ?? 4}
                          onChange={(e) => setEditDraft({ ...editDraft, schedule_day: Number(e.target.value) })}
                          className="h-10 bg-[var(--bg-tertiary)] border border-[var(--border-color)] rounded-lg px-3 text-sm text-[var(--text-primary)]"
                        >
                          <option value={0}>Every day</option>
                          {DAY_LABELS.slice(1).map((label, i) => (
                            <option key={i + 1} value={i + 1}>{label}</option>
                          ))}
                        </select>
                        <select
                          value={editDraft.schedule_hour ?? 21}
                          onChange={(e) => setEditDraft({ ...editDraft, schedule_hour: Number(e.target.value) })}
                          className="h-10 bg-[var(--bg-tertiary)] border border-[var(--border-color)] rounded-lg px-3 text-sm text-[var(--text-primary)]"
                        >
                          {Array.from({ length: 24 }, (_, i) => (
                            <option key={i} value={i}>{i.toString().padStart(2, '0')}</option>
                          ))}
                        </select>
                        <span className="flex items-center text-[var(--text-muted)] text-sm">:</span>
                        <select
                          value={editDraft.schedule_minute ?? 0}
                          onChange={(e) => setEditDraft({ ...editDraft, schedule_minute: Number(e.target.value) })}
                          className="h-10 bg-[var(--bg-tertiary)] border border-[var(--border-color)] rounded-lg px-3 text-sm text-[var(--text-primary)]"
                        >
                          {[0, 5, 10, 15, 20, 25, 30, 35, 36, 40, 45, 50, 55].map((m) => (
                            <option key={m} value={m}>{m.toString().padStart(2, '0')}</option>
                          ))}
                        </select>
                        <span className="flex items-center text-[var(--text-muted)] text-xs">UTC</span>
                      </div>
                      <p className="text-xs text-[var(--text-muted)] mt-1">
                        = {formatScheduleLocal(
                          editDraft.schedule_day ?? 4,
                          editDraft.schedule_hour ?? 21,
                          editDraft.schedule_minute ?? 36,
                          notif.timezone
                        )}
                      </p>
                    </div>
                    )}

                    {/* Email Recipients (for email-type notifications) */}
                    {isEmail && (
                      <>
                      <div>
                        <label className="block text-xs font-medium text-[var(--text-muted)] mb-1.5 uppercase tracking-wide">Email Recipients</label>

                        {/* Selected recipients */}
                        {editEmailRecipients.length > 0 && (
                          <div className="flex flex-wrap gap-2 mb-2">
                            {editEmailRecipients.map((email) => (
                              <span key={email} className="inline-flex items-center gap-1 px-2 py-1 bg-[var(--bg-tertiary)] rounded text-xs text-[var(--text-primary)]">
                                {email}
                                <button
                                  onClick={() => setEditEmailRecipients(editEmailRecipients.filter((e) => e !== email))}
                                  className="text-red-400 hover:text-red-300 ml-1"
                                >×</button>
                              </span>
                            ))}
                          </div>
                        )}

                        {/* Add email input */}
                        <div className="flex gap-2">
                          <input
                            type="email"
                            placeholder="Add email address..."
                            value={newEmailInput}
                            onChange={(e) => setNewEmailInput(e.target.value)}
                            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addEmailRecipient() } }}
                            className="flex-1 h-9 bg-[var(--bg-tertiary)] border border-[var(--border-color)] rounded-lg px-3 text-sm text-[var(--text-primary)]"
                          />
                          <button
                            onClick={addEmailRecipient}
                            className="px-3 h-9 text-xs font-medium bg-[var(--bg-tertiary)] text-[var(--text-primary)] rounded-lg hover:bg-[var(--border-color)] transition-colors"
                          >
                            Add
                          </button>
                        </div>
                      </div>

                      {/* CC Recipients */}
                      <div>
                        <label className="block text-xs font-medium text-[var(--text-muted)] mb-1.5 uppercase tracking-wide">CC</label>

                        {editEmailCc.length > 0 && (
                          <div className="flex flex-wrap gap-2 mb-2">
                            {editEmailCc.map((email) => (
                              <span key={email} className="inline-flex items-center gap-1 px-2 py-1 bg-[var(--bg-tertiary)] rounded text-xs text-[var(--text-primary)]">
                                {email}
                                <button
                                  onClick={() => setEditEmailCc(editEmailCc.filter((e) => e !== email))}
                                  className="text-red-400 hover:text-red-300 ml-1"
                                >×</button>
                              </span>
                            ))}
                          </div>
                        )}

                        <div className="flex gap-2">
                          <input
                            type="email"
                            placeholder="Add CC address..."
                            value={newCcInput}
                            onChange={(e) => setNewCcInput(e.target.value)}
                            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addCcRecipient() } }}
                            className="flex-1 h-9 bg-[var(--bg-tertiary)] border border-[var(--border-color)] rounded-lg px-3 text-sm text-[var(--text-primary)]"
                          />
                          <button
                            onClick={addCcRecipient}
                            className="px-3 h-9 text-xs font-medium bg-[var(--bg-tertiary)] text-[var(--text-primary)] rounded-lg hover:bg-[var(--border-color)] transition-colors"
                          >
                            Add
                          </button>
                        </div>
                      </div>
                      </>
                    )}

                    {/* Channels (for Slack-type notifications) */}
                    {!isEmail && (
                      <>
                        <div>
                          <label className="block text-xs font-medium text-[var(--text-muted)] mb-1.5 uppercase tracking-wide">Channels</label>

                          {/* Selected */}
                          {editChannels.length > 0 && (
                            <div className="flex flex-wrap gap-2 mb-2">
                              {editChannels.map((chId) => (
                                <span key={chId} className="inline-flex items-center gap-1 px-2 py-1 bg-[var(--bg-tertiary)] rounded text-xs text-[var(--text-primary)]">
                                  {getChannelName(chId)}
                                  <button
                                    onClick={() => setEditChannels(editChannels.filter((c) => c !== chId))}
                                    className="text-red-400 hover:text-red-300 ml-1"
                                  >×</button>
                                </span>
                              ))}
                            </div>
                          )}

                          {/* Search */}
                          <input
                            type="text"
                            placeholder="Search channels to add..."
                            value={channelSearch}
                            onChange={(e) => {
                              setChannelSearch(e.target.value)
                              loadSlackChannels()
                            }}
                            className="w-full h-9 bg-[var(--bg-tertiary)] border border-[var(--border-color)] rounded-lg px-3 text-sm text-[var(--text-primary)]"
                          />
                          {channelSearch && availableChannels.length > 0 && (
                            <div className="mt-1 max-h-32 overflow-y-auto border border-[var(--border-color)] rounded-lg bg-[var(--bg-secondary)]">
                              {availableChannels
                                .filter((ch) =>
                                  !ch.is_archived && ch.num_members > 0 &&
                                  ch.name.toLowerCase().includes(channelSearch.toLowerCase()) &&
                                  !editChannels.includes(ch.id)
                                )
                                .slice(0, 10)
                                .map((ch) => (
                                  <button
                                    key={ch.id}
                                    onClick={() => { setEditChannels([...editChannels, ch.id]); setChannelSearch('') }}
                                    className="w-full px-3 py-1.5 text-left hover:bg-[var(--bg-tertiary)] text-xs text-[var(--text-primary)]"
                                  >
                                    #{ch.name} <span className="text-[var(--text-muted)]">({ch.num_members})</span>
                                  </button>
                                ))}
                            </div>
                          )}

                          {/* Paste channel ID */}
                          <div className="flex gap-2 mt-2">
                            <input
                              type="text"
                              placeholder="Paste channel or user ID (e.g. C04PFKAM8JW, U09ABC123)..."
                              value={channelIdInput}
                              onChange={(e) => setChannelIdInput(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  e.preventDefault()
                                  const id = channelIdInput.trim()
                                  if (/^[CDGU][A-Z0-9]{8,}$/.test(id) && !editChannels.includes(id)) {
                                    setEditChannels([...editChannels, id])
                                    setChannelIdInput('')
                                  }
                                }
                              }}
                              className="flex-1 h-9 bg-[var(--bg-tertiary)] border border-[var(--border-color)] rounded-lg px-3 text-sm text-[var(--text-primary)]"
                            />
                            <button
                              onClick={() => {
                                const id = channelIdInput.trim()
                                if (/^[CDGU][A-Z0-9]{8,}$/.test(id) && !editChannels.includes(id)) {
                                  setEditChannels([...editChannels, id])
                                  setChannelIdInput('')
                                }
                              }}
                              className="px-3 h-9 text-xs font-medium bg-[var(--bg-tertiary)] text-[var(--text-primary)] rounded-lg hover:bg-[var(--border-color)] transition-colors"
                            >
                              Add
                            </button>
                          </div>
                        </div>

                        {/* Mention PMs */}
                        <label className="flex items-center gap-3 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={editDraft.mention_pms ?? false}
                            onChange={(e) => setEditDraft({ ...editDraft, mention_pms: e.target.checked })}
                            className="w-4 h-4 rounded border-gray-300"
                          />
                          <span className="text-sm text-[var(--text-primary)]">Mention key people</span>
                        </label>
                      </>
                    )}

                    {/* Save / Cancel */}
                    <div className="flex gap-2 pt-2">
                      <button
                        onClick={() => saveNotification(notif.id)}
                        disabled={saving}
                        className="px-4 py-2 bg-primary text-white rounded-lg text-sm font-medium hover:bg-primary/90 disabled:opacity-50"
                      >
                        {saving ? 'Saving...' : 'Save'}
                      </button>
                      <button
                        onClick={cancelEditing}
                        className="px-4 py-2 bg-[var(--bg-tertiary)] text-[var(--text-primary)] rounded-lg text-sm font-medium hover:bg-[var(--border-color)]"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}

      {/* Key People Tab */}
      {activeTab === 'pms' && (
        <div className="space-y-6">
          <p className="text-sm text-[var(--text-muted)]">
            Key people who can be mentioned in notifications
          </p>
          <div className="overflow-x-auto">
            <table className="w-full border border-[var(--border-color)] rounded-lg">
              <thead className="bg-[var(--bg-secondary)]">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-medium text-[var(--text-primary)]">Name</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-[var(--text-primary)]">Email</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-[var(--text-primary)]">Slack</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-[var(--text-primary)]">Status</th>
                </tr>
              </thead>
              <tbody>
                {productManagers.map((pm) => (
                  <tr key={pm.id} className="border-t border-[var(--border-color)]">
                    <td className="px-4 py-3 text-sm text-[var(--text-primary)]">{pm.name}</td>
                    <td className="px-4 py-3 text-sm text-[var(--text-muted)]">{pm.email}</td>
                    <td className="px-4 py-3 text-sm text-[var(--text-muted)]">
                      {pm.slack_username ? `@${pm.slack_username}` : <span className="italic">Not synced</span>}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                        pm.is_active ? 'bg-green-500/20 text-green-400' : 'bg-gray-500/20 text-gray-400'
                      }`}>
                        {pm.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Preview Modal */}
      {previewHtml && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[60]">
          <div className="bg-white rounded-lg shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
            {/* Preview Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-gray-50">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Notification Preview</h3>
                <p className="text-xs text-gray-500 mt-0.5">This is how the notification will appear when sent</p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => {
                    if (!previewHtml) return
                    const printWindow = window.open('', '_blank')
                    if (!printWindow) return
                    printWindow.document.write(previewHtml)
                    printWindow.document.close()
                    printWindow.focus()
                    // Small delay to let content render before print dialog
                    setTimeout(() => printWindow.print(), 300)
                  }}
                  className="px-3 py-1.5 text-xs font-medium bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                >
                  Export PDF
                </button>
                <button
                  onClick={() => {
                    if (previewIframeRef.current?.contentDocument?.body) {
                      const text = previewIframeRef.current.contentDocument.body.innerText
                      navigator.clipboard.writeText(text).then(() => {
                        setSuccess('Copied to clipboard')
                        setTimeout(() => setSuccess(null), 2000)
                      })
                    }
                  }}
                  className="px-3 py-1.5 text-xs font-medium bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-colors"
                >
                  Copy to Clipboard
                </button>
                <button
                  onClick={() => setPreviewHtml(null)}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
            {/* Preview Content */}
            <div className="flex-1 overflow-hidden bg-gray-100">
              <iframe
                ref={previewIframeRef}
                srcDoc={previewHtml}
                className="w-full h-full border-0"
                style={{ minHeight: '500px' }}
                title="Email Preview"
                sandbox="allow-same-origin"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
